import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { fetchGroupChatGetChannel } from "../utils/api/fetchGroupChatGetChannel";
import {
  TGroupChatChannel,
  TGroupChatSendbirdUser,
} from "../api/group_chat/get_channel";
import { fetchGroupChatGetHistory } from "../utils/api/fetchGroupChatGetHistory";
import { fetchGroupChatDownloadFile } from "../utils/api/fetchGroupChatDownloadFile";
import {
  TGroupChatHistoryMessage,
  TGroupChatMessageFile,
} from "../api/group_chat/get_history";
import { PAGE_LIMIT } from "../utils/chat/messageHistoryUtils";
import {
  doDownloadFile,
  parseMessageHistoryFromDate,
  parseMessageHistoryMessageId,
} from "../utils/chat/messageHistoryHelpers";

type UseMessageHistoryParams = {
  channelId: number;
  messageIdParam: string | string[] | undefined | null;
  fromDateParam: string | string[] | undefined | null;
};

export const useMessageHistory = ({
  channelId,
  messageIdParam,
  fromDateParam,
}: UseMessageHistoryParams) => {
  const [searchWord, setSearchWord] = useState<string | undefined>(undefined);
  const [channel, setChannel] = useState<TGroupChatChannel | null>(null);
  const [data, setData] = useState<TGroupChatHistoryMessage[]>([]);
  const [sendbirdUsers, setSendbirdUsers] = useState<
    TGroupChatSendbirdUser[] | null
  >(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const lastCreatedAtRef = useRef<string | undefined>(undefined);
  const lastSendbirdMessageIdRef = useRef<number | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const previousScrollHeightRef = useRef<number | null>(null);
  const isFetchingRef = useRef(false);

  const messageId = useMemo(
    () => parseMessageHistoryMessageId(messageIdParam),
    [messageIdParam],
  );

  const fromDate = useMemo(
    () => parseMessageHistoryFromDate(fromDateParam),
    [fromDateParam],
  );

  // チャンネル情報を取得
  useEffect(() => {
    if (!channelId) {
      return;
    }

    const getChannel = async () => {
      const response = await fetchGroupChatGetChannel({
        channel_id: channelId,
      });
      if (!response.isSuccess || !response.result) {
        setErrorMessage("トークルーム情報の取得に失敗しました。");
        return;
      }
      setChannel(response.result.channel ?? null);
      setSendbirdUsers(response.result.sendbird_users ?? null);
    };

    getChannel();
    setData([]);
    setHasMore(true);
    setSearchWord(undefined);
    lastCreatedAtRef.current = fromDate;
    lastSendbirdMessageIdRef.current = messageId;
  }, [channelId, messageId, fromDate]);

  // メッセージ履歴を取得
  const getMessageHistory = useCallback(async () => {
    if (isFetchingRef.current) {
      return;
    }
    isFetchingRef.current = true;
    setIsLoading(true);
    try {
      const response = await fetchGroupChatGetHistory({
        channel_id: channelId,
        last_sendbird_message_id: lastSendbirdMessageIdRef.current ?? undefined,
        last_created_at: lastCreatedAtRef.current ?? undefined,
        search_word: searchWord ?? undefined,
        page_limit: PAGE_LIMIT,
      });
      if (!response.isSuccess || !response.result) {
        setErrorMessage("メッセージ履歴の取得に失敗しました。");
        setHasMore(false);
        return;
      }
      const newMessages = response.result.message_list ?? [];
      if (newMessages.length > 0) {
        const lastMessage = newMessages[newMessages.length - 1];
        lastCreatedAtRef.current = lastMessage.created_at;
        lastSendbirdMessageIdRef.current = lastMessage.sendbird_message_id;
        newMessages.reverse();
        setData((prev) => [...newMessages, ...prev]);
      }
      if (newMessages.length === 0 || newMessages.length < PAGE_LIMIT) {
        setHasMore(false);
      }
    } finally {
      isFetchingRef.current = false;
      setIsLoading(false);
    }
  }, [channelId, searchWord]);

  // スクロール位置を保持
  useLayoutEffect(() => {
    if (containerRef.current && previousScrollHeightRef.current) {
      const currentScrollHeight = containerRef.current.scrollHeight;
      const heightDifference =
        currentScrollHeight - previousScrollHeightRef.current;
      containerRef.current.scrollTop = heightDifference;
      previousScrollHeightRef.current = null;
    }
  }, [data]);

  // 無限スクロール
  useEffect(() => {
    const observer = new IntersectionObserver(
      async (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          previousScrollHeightRef.current =
            containerRef?.current?.scrollHeight ?? 0;
          getMessageHistory();
        }
      },
      { root: containerRef.current, threshold: 1.0 },
    );

    const currentSentinel = sentinelRef.current;
    if (currentSentinel) {
      observer.observe(currentSentinel);
    }
    return () => {
      if (currentSentinel) {
        observer.unobserve(currentSentinel);
      }
    };
  }, [isLoading, getMessageHistory, hasMore]);

  // ファイルをダウンロード
  const downloadFile = useCallback(
    async (file: TGroupChatMessageFile) => {
      if (!file.id || !channelId) return;
      const response = await fetchGroupChatDownloadFile({
        channel_id: channelId,
        file_id: file.id,
      });
      if (!response.isSuccess || !response.result?.url) {
        setErrorMessage("ファイルのダウンロードに失敗しました。");
        return;
      }
      doDownloadFile(response.result.url, file.file_name);
    },
    [channelId],
  );

  // 履歴のスクロールを最下部に移動
  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (!containerRef.current) {
        return;
      }
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    });
  }, []);

  // メッセージ履歴を取得し、スクロールを最下部に移動
  useEffect(() => {
    if (!channelId) {
      return;
    }
    const loadMessageHistory = async () => {
      setData([]);
      setHasMore(true);
      lastCreatedAtRef.current = fromDate;
      lastSendbirdMessageIdRef.current = messageId;
      await getMessageHistory();
      scrollToBottom();
    };
    loadMessageHistory();
  }, [
    messageId,
    fromDate,
    searchWord,
    getMessageHistory,
    channelId,
    scrollToBottom,
  ]);

  // 検索ワードを設定
  const handleSearch = (word: string | undefined) => {
    setSearchWord(word ?? "");
  };

  const clearError = () => setErrorMessage(null);

  return {
    channel,
    sendbirdUsers,
    data,
    isLoading,
    containerRef,
    sentinelRef,
    downloadFile,
    handleSearch,
    errorMessage,
    clearError,
  };
};
